import { useState } from 'react';
import type { Manifest } from '../content/data';
import { navigate } from '../navigation/routes';
import { useProgressActions } from '../progress/progress-context';

const daySteps = [
  ['Learn', 'Read for a fixed time, then run the experiments.'],
  ['Mini project', 'Build a small product in a separate folder.'],
  ['Main project', 'Add one bounded slice to the current main project.'],
  ['Review', 'Answer aloud, record evidence, and close the day.']
] as const;

const onboardingSteps = ['The sequence', 'A working day', 'The ground rule'] as const;

export function Onboarding({ manifest }: { manifest: Manifest }) {
  const [step, setStep] = useState(0);
  const [understood, setUnderstood] = useState(false);
  const { finishOnboarding } = useProgressActions();

  const start = () => {
    finishOnboarding();
    navigate({ page: 'today' });
  };

  return (
    <main className="onboarding" id="main-content">
      <aside className="onboarding-spine" aria-label="Onboarding progress">
        <a className="onboarding-brand" href="#/today" aria-label="Frontend Fieldbook home">
          Frontend
          <span>Fieldbook</span>
        </a>
        <ol>
          {onboardingSteps.map((label, index) => (
            <li className={index === step ? 'is-current' : index < step ? 'is-done' : ''} key={label}>
              <span>{index + 1}</span>
              {label}
            </li>
          ))}
        </ol>
        <p className="onboarding-step-copy">Step {step + 1} of 3: <strong>{onboardingSteps[step]}</strong></p>
        <p className="onboarding-data-state">
          {manifest.completeCurriculum
            ? 'All 14 weeks are loaded.'
            : `${manifest.totals.weeks}-week review data is loaded.`}
        </p>
      </aside>

      <section className="onboarding-panel">
        {step === 0 ? (
          <div className="onboarding-copy">
            <h1>Work on one day. Keep the full path nearby.</h1>
            <p className="lead">
              The fieldbook turns the curriculum into a daily sequence. It remembers your checklists,
              interview attempts, notes, and retrospectives in this browser.
            </p>
            <dl className="onboarding-facts">
              <div>
                <dt>{manifest.totals.days}</dt>
                <dd>days loaded</dd>
              </div>
              <div>
                <dt>{manifest.totals.weekdayMiniProjects}</dt>
                <dd>weekday mini projects</dd>
              </div>
              <div>
                <dt>{manifest.totals.interviewQuestions}</dt>
                <dd>interview questions</dd>
              </div>
            </dl>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="onboarding-copy">
            <h1>A working day has four stops.</h1>
            <p className="lead">Only one stop is open in the main reading area at a time.</p>
            <ol className="onboarding-day-steps">
              {daySteps.map(([title, description]) => (
                <li key={title}>
                  <strong>{title}</strong>
                  <span>{description}</span>
                </li>
              ))}
            </ol>
            <p className="plain-note">
              Weekdays take 4 to 6 hours. Saturday is the longer build day. Sunday is for retrieval,
              reflection, and planning only.
            </p>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="onboarding-copy">
            <h1>Your place follows completion, not the calendar.</h1>
            <p className="lead">
              If Tuesday is unfinished, Tuesday stays on the Today page tomorrow. The app does not turn a
              missed date into missing work.
            </p>
            <div className="ground-rule">
              <p>To close a day, finish its pass checklist, attempt the assigned questions, and write the three short reflection answers.</p>
              <p>Your data stays in this browser. Settings lets you export a backup.</p>
            </div>
            <label className="agreement">
              <input
                type="checkbox"
                checked={understood}
                onChange={(event) => setUnderstood(event.currentTarget.checked)}
              />
              <span>I understand that unfinished work stays as today's work.</span>
            </label>
          </div>
        ) : null}

        <div className="onboarding-actions">
          {step > 0 ? (
            <button className="secondary-button" type="button" onClick={() => setStep((current) => current - 1)}>
              Back
            </button>
          ) : <span />}
          {step < 2 ? (
            <button className="primary-button" type="button" onClick={() => setStep((current) => current + 1)}>
              Continue
            </button>
          ) : (
            <button className="primary-button" type="button" disabled={!understood} onClick={start}>
              Open day 1
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
